import { useMemo, useState } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Option } from "@/components/dropdown/Option";
import { MathThemeContext, useMathTheme } from "@/components/expression/theme";
import { themes } from "@/components/expression/theme/corca-themes";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { MathThemePreview } from "@/components/sidebar/math-theme-switcher/MathThemePreview";
import { themeSelector } from "@/components/sidebar/math-theme-switcher/previews";
import { idGenerator } from "@/components/suggestions/idGenerator";
import { DocumentModel } from "@/models/DocumentModel";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { useStore } from "@/store";
import { DEFAULT_THEME } from "@/store/UserSettingsStore";

import type { NodeModel } from "@/models/nodes/types";

export const MathThemeSwitcher = observer(function MathThemeSwitcher() {
  const t = useTranslations("mathTheme");
  const currentTheme = useMathTheme();
  const { userSettings, view } = useStore();
  const [nodes, setNodes] = useState<NodeModel[]>([]);

  const previewDoc = useMemo(() => {
    const newDoc = new DocumentModel("mathThemePreview", null);
    newDoc.idGenerator = idGenerator;
    return newDoc;
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (open && nodes.length === 0) {
      const previewNodes = runInAction(() =>
        themeSelector
          .map((preview) => {
            return createNodeFrom(preview, previewDoc);
          })
          .filter((node) => !!node),
      );
      setNodes(previewNodes);
    }
  };

  const items = themes.map((theme, i) => {
    let title = theme.intlName ? t(theme.intlName) : theme.id;
    if (theme.id === DEFAULT_THEME.id) {
      title = t("defaultTheme", { title });
    }
    return {
      title,
      active: theme.id === currentTheme.id,
      component: (
        <Option
          title={title}
          label={title}
          subTitle={
            <MathThemeContext.Provider value={theme}>
              <MathThemePreview doc={previewDoc} nodes={nodes} className="inline-flex" />
            </MathThemeContext.Provider>
          }
          checked={theme.id === currentTheme.id}
          onClick={() => userSettings.setMathTheme(theme)}
          truncate={false}
          className="data-[focus]:bg-sidebar-button-active-bg"
        />
      ),
      footer: () =>
        i !== themes.length - 1 &&
        theme.sidebarGroupKey !== themes[i + 1]?.sidebarGroupKey && <hr className="my-1 mx-2" />,
      onClick: () => userSettings.setMathTheme(theme),
    };
  });

  return (
    <Dropdown
      onOpenChange={handleOpenChange}
      toggleButtonComponent={
        <Sidebar.Button
          as="div"
          className="rounded-8"
          icon={<Icon name="MathTheme" />}
          title={t("title")}
          hintKey="mathTheme"
          disableTooltip={view.isHintsPanelVisible}
        />
      }
      items={items}
    />
  );
});
